import { notEmpty } from '@mahawi/eshop-common/src/not-empty';
import {
  type IAdminCategory,
  type IComparisonPortal,
  type IComparisonPortalCategory,
  type IComparisonPortalTypeCategory,
} from '@mahawi/eshop-common/src/types';
import { type RootState } from '@reducers';
import { Button, Col, Form, Input, Row, Select, Skeleton, Space } from 'antd';
import { type DefaultOptionType } from 'antd/lib/select';
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { getFilterSort } from '../../fragments/select-filter';
import { useAppDispatch } from '../../hooks/use-app-dispatch';
import { saveComparisonPortalCategory } from '../../reducers/comparison-portal/reducer';
import { SPACING } from '../../theme/tokens';

interface IForm {
  comparisonPortalCategoryUUID: string;
  categoryUUID: string;
}

interface PortalFormProps {
  comparisonPortal: IComparisonPortal;
  category: IAdminCategory;
  comparisonPortalCategories: IComparisonPortalCategory[];
}

function PortalForm({
  comparisonPortal,
  category,
  comparisonPortalCategories,
}: PortalFormProps): React.ReactElement {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm<IForm>();

  const selectOptions = comparisonPortalCategories
    .map((cat: IComparisonPortalCategory): DefaultOptionType | null => {
      if (cat.comparisonPortalType.uuid !== comparisonPortal.uuid) {
        return null;
      }
      return { value: cat.uuid, label: cat.name };
    })
    .filter(notEmpty);

  const initialValues: IForm = {
    categoryUUID: category.uuid,
    comparisonPortalCategoryUUID:
      category.comparisonPortalTypes.find(
        (t: IComparisonPortalTypeCategory): boolean =>
          t.uuid === comparisonPortal.uuid,
      )?.category.uuid ?? '',
  };

  const onFinish = useCallback(
    (values: IForm): void => {
      dispatch(
        saveComparisonPortalCategory(
          values.categoryUUID,
          values.comparisonPortalCategoryUUID,
        ),
      );
    },
    [dispatch],
  );

  return (
    <Form form={form} initialValues={initialValues} onFinish={onFinish}>
      <Row gutter={SPACING.lg}>
        <Col span={24}>
          <Space size={SPACING.lg} align="start">
            {comparisonPortal.name}
          </Space>
        </Col>

        <Col span={21}>
          <Form.Item name="categoryUUID" hidden>
            <Input type="hidden" />
          </Form.Item>

          <Form.Item
            name="comparisonPortalCategoryUUID"
            rules={[
              {
                required: true,
                message: 'Please select a comparison portal category',
              },
            ]}
          >
            <Select
              size="large"
              placeholder="Select a comparison portal category"
              showSearch={{
                autoClearSearchValue: false,
                filterOption: (
                  input: string,
                  option: DefaultOptionType | undefined,
                ): boolean => {
                  const labelText: string = String(
                    option ? option.label : '',
                  ).toLowerCase();
                  return labelText.includes(input.toLowerCase());
                },
                filterSort: getFilterSort,
              }}
              options={selectOptions}
            />
          </Form.Item>
        </Col>

        <Col span={3}>
          <Button type="primary" size="large" color="primary" htmlType="submit">
            Save
          </Button>
        </Col>
      </Row>
    </Form>
  );
}

function ComparisonPortalsCategoriesComponent(): React.ReactElement {
  const comparisonPortalPortals = useSelector(
    (s: RootState) => s.ComparisonPortal.portals,
  );
  const comparisonPortalCategories = useSelector(
    (s: RootState) => s.ComparisonPortal.categories,
  );
  const category = useSelector((s: RootState) => s.Category.category);

  return (
    <>
      {comparisonPortalPortals && category && comparisonPortalCategories ? (
        comparisonPortalPortals.map(
          (comparisonPortal: IComparisonPortal): React.ReactElement => (
            <PortalForm
              key={comparisonPortal.code}
              comparisonPortal={comparisonPortal}
              category={category}
              comparisonPortalCategories={comparisonPortalCategories}
            />
          ),
        )
      ) : (
        <Skeleton active />
      )}
    </>
  );
}

export default ComparisonPortalsCategoriesComponent;
